import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { io } from 'socket.io-client';
import api from '../api/client';

const RED = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);

function numColor(n) {
  if (n === null || n === undefined) return 'green';
  if (n === 0) return 'green';
  return RED.has(n) ? 'red' : 'black';
}

const COLOR_CLS = {
  red:   'bg-red-600',
  black: 'bg-slate-800',
  green: 'bg-green-600',
};
const TEXT_CLS = {
  red:   'text-red-400',
  black: 'text-slate-300',
  green: 'text-green-400',
};

/* ─── Wheel ─── */
const WHEEL_ORDER = [0,32,15,19,4,21,2,25,17,34,6,27,13,36,11,30,8,23,10,5,24,16,33,1,20,14,31,9,22,18,29,7,28,12,35,3,26];

function RouletteWheel({ spinning, result }) {
  const [angle, setAngle] = useState(0);
  const [ballAngle, setBallAngle] = useState(0);
  const animRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (spinning) {
      startRef.current = Date.now();
      const DURATION = 5000;
      const TARGET_WHEEL = 360 * 8 + Math.random() * 360;
      const TARGET_BALL = -(360 * 12 + Math.random() * 360);

      function frame() {
        const elapsed = Date.now() - startRef.current;
        const t = Math.min(elapsed / DURATION, 1);
        // Ease out cubic
        const ease = 1 - Math.pow(1 - t, 3);
        setAngle(ease * TARGET_WHEEL);
        setBallAngle(ease * TARGET_BALL);
        if (t < 1) animRef.current = requestAnimationFrame(frame);
      }
      animRef.current = requestAnimationFrame(frame);
    } else if (result !== null && result !== undefined) {
      // Snap to result position
      const idx = WHEEL_ORDER.indexOf(result);
      const targetAngle = -(idx / WHEEL_ORDER.length) * 360;
      setBallAngle(targetAngle);
    }
    return () => cancelAnimationFrame(animRef.current);
  }, [spinning, result]);

  const size = 220;
  const cx = size / 2, cy = size / 2;
  const outerR = 100, innerR = 68, ballR = 88;
  const segAngle = 360 / WHEEL_ORDER.length;

  return (
    <div className="flex justify-center select-none">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <defs>
            <filter id="shadow">
              <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#000" floodOpacity="0.5" />
            </filter>
          </defs>

          {/* Outer ring */}
          <circle cx={cx} cy={cy} r={outerR + 8} fill="#1a1a1a" filter="url(#shadow)" />
          <circle cx={cx} cy={cy} r={outerR + 4} fill="#2a2a2a" />

          {/* Segments */}
          <g transform={`rotate(${angle} ${cx} ${cy})`}>
            {WHEEL_ORDER.map((num, i) => {
              const startA = (i * segAngle - 90) * (Math.PI / 180);
              const endA = ((i + 1) * segAngle - 90) * (Math.PI / 180);
              const x1 = cx + outerR * Math.cos(startA);
              const y1 = cy + outerR * Math.sin(startA);
              const x2 = cx + outerR * Math.cos(endA);
              const y2 = cy + outerR * Math.sin(endA);
              const xi1 = cx + innerR * Math.cos(startA);
              const yi1 = cy + innerR * Math.sin(startA);
              const xi2 = cx + innerR * Math.cos(endA);
              const yi2 = cy + innerR * Math.sin(endA);
              const color = numColor(num);
              const fill = color === 'red' ? '#dc2626' : color === 'green' ? '#16a34a' : '#1e1e1e';

              const midA = ((i + 0.5) * segAngle - 90) * (Math.PI / 180);
              const tx = cx + (innerR + (outerR - innerR) * 0.55) * Math.cos(midA);
              const ty = cy + (innerR + (outerR - innerR) * 0.55) * Math.sin(midA);

              return (
                <g key={num}>
                  <path
                    d={`M ${xi1} ${yi1} L ${x1} ${y1} A ${outerR} ${outerR} 0 0 1 ${x2} ${y2} L ${xi2} ${yi2} A ${innerR} ${innerR} 0 0 0 ${xi1} ${yi1} Z`}
                    fill={fill}
                    stroke="#111"
                    strokeWidth="0.5"
                  />
                  <text
                    x={tx} y={ty}
                    textAnchor="middle" dominantBaseline="middle"
                    fill="white" fontSize="7" fontWeight="bold"
                    transform={`rotate(${(i + 0.5) * segAngle + 90} ${tx} ${ty})`}
                  >
                    {num}
                  </text>
                </g>
              );
            })}
          </g>

          {/* Inner circle */}
          <circle cx={cx} cy={cy} r={innerR - 2} fill="#111" />
          <circle cx={cx} cy={cy} r={innerR - 6} fill="#1a1a1a" />

          {/* Ball */}
          {(spinning || result !== null) && (
            <g transform={`rotate(${ballAngle} ${cx} ${cy})`}>
              <circle cx={cx} cy={cy - ballR} r="5" fill="white" filter="url(#shadow)" />
            </g>
          )}

          {/* Center pin */}
          <circle cx={cx} cy={cy} r={8} fill="#333" />
          <circle cx={cx} cy={cy} r={4} fill="#555" />
        </svg>

        {/* Result in center */}
        {!spinning && result !== null && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${COLOR_CLS[numColor(result)]}`}>
              {result}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── History ─── */
function History({ history }) {
  if (!history?.length) return null;
  return (
    <div className="flex gap-1.5 flex-wrap justify-center">
      {history.map((h, i) => (
        <div key={i} className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${COLOR_CLS[h.color]}`}>
          {h.number}
        </div>
      ))}
    </div>
  );
}

/* ─── Number grid ─── */
const GRID = [
  [3,6,9,12,15,18,21,24,27,30,33,36],
  [2,5,8,11,14,17,20,23,26,29,32,35],
  [1,4,7,10,13,16,19,22,25,28,31,34],
];

function BettingBoard({ onBet, disabled, myBets, betAmount }) {
  function btn(label, type, value, cls = '', span = 1) {
    const hasBet = myBets.some(b => b.type === type && String(b.value) === String(value));
    return (
      <button
        key={`${type}-${value}`}
        onClick={() => !disabled && onBet(type, value)}
        disabled={disabled}
        className={`${cls} border border-slate-600 rounded text-xs font-bold py-1 flex items-center justify-center transition-all
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-125 cursor-pointer'}
          ${hasBet ? 'ring-2 ring-yellow-400 ring-offset-1 ring-offset-slate-900' : ''}
        `}
        style={{ gridColumn: `span ${span}` }}
      >
        {label}
        {hasBet && <span className="ml-1 text-yellow-400 text-[10px]">✓</span>}
      </button>
    );
  }

  return (
    <div className="space-y-1.5 w-full max-w-lg mx-auto">
      {/* Zero */}
      <div className="grid grid-cols-13 gap-1">
        <button
          onClick={() => !disabled && onBet('number', 0)}
          disabled={disabled}
          className={`col-span-1 bg-green-700 hover:bg-green-600 border border-green-500 rounded text-white text-xs font-bold py-3 flex items-center justify-center
            ${myBets.some(b => b.type === 'number' && b.value === 0) ? 'ring-2 ring-yellow-400' : ''}
            ${disabled ? 'opacity-50' : 'cursor-pointer'}
          `}
          style={{ gridRow: 'span 3' }}
        >
          0
        </button>

        {/* Number grid */}
        <div className="col-span-12 grid grid-cols-12 gap-1">
          {GRID.map((row, ri) => (
            <React.Fragment key={ri}>
              {row.map(n => {
                const c = numColor(n);
                const hasBet = myBets.some(b => b.type === 'number' && b.value === n);
                return (
                  <button
                    key={n}
                    onClick={() => !disabled && onBet('number', n)}
                    disabled={disabled}
                    className={`${c === 'red' ? 'bg-red-700 hover:bg-red-600' : 'bg-slate-700 hover:bg-slate-600'}
                      border border-slate-600 rounded text-white text-xs font-bold py-2 transition-all
                      ${hasBet ? 'ring-2 ring-yellow-400' : ''}
                      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    {n}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Bottom bets */}
      <div className="grid grid-cols-3 gap-1">
        {btn('1–12', 'dozen', '1st', 'bg-slate-700 text-slate-200 py-2')}
        {btn('13–24', 'dozen', '2nd', 'bg-slate-700 text-slate-200 py-2')}
        {btn('25–36', 'dozen', '3rd', 'bg-slate-700 text-slate-200 py-2')}
      </div>
      <div className="grid grid-cols-6 gap-1">
        {btn('1–18', 'half', 'low', 'bg-slate-600 text-slate-200 py-2')}
        {btn('Páros', 'evenodd', 'even', 'bg-slate-600 text-slate-200 py-2')}
        {btn('Piros', 'color', 'red', 'bg-red-700 text-white py-2')}
        {btn('Fekete', 'color', 'black', 'bg-slate-800 text-white border-slate-500 py-2')}
        {btn('Páratlan', 'evenodd', 'odd', 'bg-slate-600 text-slate-200 py-2')}
        {btn('19–36', 'half', 'high', 'bg-slate-600 text-slate-200 py-2')}
      </div>
    </div>
  );
}

/* ─── Bet amount selector ─── */
const BET_AMOUNTS = [25, 50, 100, 250, 500, 1000, 2500, 5000];

/* ─── Main ─── */
export default function Roulette() {
  const { user, updateBalance } = useAuth();
  const navigate = useNavigate();
  const [game, setGame] = useState({ state: 'betting', countdown: 25, result: null, history: [], betCount: 0 });
  const [myBets, setMyBets] = useState([]);
  const [betAmount, setBetAmount] = useState(100);
  const [error, setError] = useState('');
  const [myPayouts, setMyPayouts] = useState([]);
  const socketRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const URL = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : '';
    const s = io(`${URL}/roulette`, { auth: { token } });
    socketRef.current = s;

    s.on('game_state', (g) => {
      setGame(g);
      if (g.state === 'betting') setMyBets([]);
    });
    s.on('countdown', (c) => setGame(prev => ({ ...prev, countdown: c })));
    s.on('bet_placed', (data) => setGame(prev => ({ ...prev, betCount: data.betCount })));
    s.on('payouts', (payouts) => {
      const mine = payouts.filter(p => p.userId === user.id);
      if (mine.length) {
        setMyPayouts(mine);
        setTimeout(() => {
          setMyPayouts([]);
          api.get('/wallet/balance').then(r => updateBalance(r.data.balance));
        }, 7000);
      }
    });
    s.on('error', (msg) => { setError(msg); setTimeout(() => setError(''), 3000); });

    return () => s.disconnect();
  }, []);

  function placeBet(type, value) {
    if (game.state !== 'betting') return;
    socketRef.current?.emit('place_bet', { type, value, amount: betAmount });
    setMyBets(prev => [...prev, { type, value, amount: betAmount }]);
  }

  const isBetting = game.state === 'betting';
  const isSpinning = game.state === 'spinning';
  const isResult = game.state === 'result';

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/')} className="text-slate-400 hover:text-slate-200 text-sm">← Lobby</button>
          <h1 className="text-2xl font-bold">🎡 Rulett</h1>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-2 text-sm mb-4">
            {error}
          </div>
        )}

        {/* Wheel + status */}
        <div className="card mb-4 flex flex-col items-center gap-4">
          <RouletteWheel spinning={isSpinning} result={isResult || (!isSpinning && !isBetting) ? game.result : null} />

          {/* Status */}
          <div className="text-center">
            {isBetting && (
              <div>
                <p className="text-green-400 font-semibold">Fogadj most!</p>
                <p className="text-3xl font-bold text-yellow-400 tabular-nums">{game.countdown}s</p>
                <p className="text-slate-500 text-xs mt-1">{game.betCount} fogadás leadva</p>
              </div>
            )}
            {isSpinning && (
              <p className="text-yellow-400 font-semibold animate-pulse text-lg">🎡 A kerék forog...</p>
            )}
            {isResult && game.result !== null && (
              <div className="flex flex-col items-center gap-2">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl ${COLOR_CLS[numColor(game.result)]}`}>
                  {game.result}
                </div>
                <p className={`font-semibold ${TEXT_CLS[numColor(game.result)]}`}>
                  {numColor(game.result) === 'red' ? 'Piros' : numColor(game.result) === 'black' ? 'Fekete' : 'Zöld'}
                  {game.result !== 0 && ` • ${game.result % 2 === 0 ? 'Páros' : 'Páratlan'}`}
                </p>
              </div>
            )}
          </div>

          {/* My payouts */}
          {myPayouts.length > 0 && (
            <div className="flex flex-col gap-1 items-center">
              {myPayouts.map((p, i) => (
                <div key={i} className={`px-4 py-2 rounded-lg font-bold text-sm ${p.net > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {p.net > 0 ? `🎉 +${Number(p.net).toLocaleString('hu')} 💰` : `😢 -${Number(Math.abs(p.net)).toLocaleString('hu')} 💰`}
                </div>
              ))}
            </div>
          )}

          {/* History */}
          {game.history?.length > 0 && (
            <div className="w-full">
              <p className="text-slate-500 text-xs text-center mb-2">Előzmények</p>
              <History history={game.history} />
            </div>
          )}
        </div>

        {/* Betting area */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-slate-300">Fogadás</p>
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
              {BET_AMOUNTS.map(b => (
                <button
                  key={b}
                  onClick={() => setBetAmount(b)}
                  className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                    betAmount === b ? 'bg-yellow-500 text-slate-900' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {b >= 1000 ? `${b/1000}K` : b}
                </button>
              ))}
            </div>
          </div>

          <BettingBoard
            onBet={placeBet}
            disabled={!isBetting}
            myBets={myBets}
            betAmount={betAmount}
          />

          {myBets.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <p className="text-xs text-slate-400">
                Leadott fogadások: <span className="text-yellow-400 font-semibold">{myBets.length} db</span>
                {' · '} Összesen: <span className="text-yellow-400 font-semibold">{myBets.reduce((s,b) => s+b.amount, 0).toLocaleString('hu')} 💰</span>
              </p>
            </div>
          )}

          {!isBetting && (
            <p className="text-center text-slate-500 text-sm mt-3">
              {isSpinning ? 'Fogadás lezárva – a kerék forog' : 'Következő kör hamarosan...'}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
